import { useEffect, useState } from "react";
import Cal, { getCalApi } from "@calcom/embed-react";
import {
    X,
    ArrowLeft,
    ArrowRight,
    Check,
    Scissors,
    Clock,
    Mail,
    PartyPopper,
} from "lucide-react";
import { useSite } from "../context/SiteContentContext";

/**
 * Two-step booking wizard.
 *  Step 1 — Choose service
 *  Step 2 — Cal.com embed (slot pick + details + confirm)
 *  Step 3 — Success screen (auto, after Cal.com fires bookingSuccessful)
 *
 * The Cal.com embed auto-resizes (no internal scroll) so the "Confirmar"
 * button is always visible. We also listen to the bookingSuccessful event
 * to show a clear success screen, removing any confusion about whether the
 * booking went through.
 */
export const BookingWizard = ({
    open,
    onClose,
    initialService = null,
}) => {
    const siteConfig = useSite();
    const [step, setStep] = useState(1);
    const [service, setService] = useState(initialService);
    const [bookingResult, setBookingResult] = useState(null);

    // Reset whenever modal opens
    useEffect(() => {
        if (open) {
            setStep(initialService ? 2 : 1);
            setService(initialService);
            setBookingResult(null);
        }
    }, [open, initialService]);

    // Lock body scroll when open
    useEffect(() => {
        if (open) {
            document.body.style.overflow = "hidden";
        } else {
            document.body.style.overflow = "";
        }
        return () => {
            document.body.style.overflow = "";
        };
    }, [open]);

    // Listen for Cal.com booking events globally (once)
    useEffect(() => {
        let mounted = true;
        (async () => {
            const cal = await getCalApi();
            cal("on", {
                action: "bookingSuccessful",
                callback: (e) => {
                    if (!mounted) return;
                    setBookingResult(e?.detail?.data || {});
                    setStep(3);
                },
            });
        })();
        return () => {
            mounted = false;
        };
    }, []);

    if (!open) return null;

    const totalSteps = 2;
    const back = () => setStep((s) => Math.max(1, s - 1));
    const next = () => setStep((s) => Math.min(totalSteps, s + 1));

    return (
        <div
            className="fixed inset-0 z-[100] flex items-stretch md:items-center md:justify-center"
            data-testid="booking-wizard-modal"
        >
            {/* Backdrop */}
            <div
                className="absolute inset-0 bg-[#0a0a0a]/90 backdrop-blur-sm"
                onClick={onClose}
            />

            {/* Dialog */}
            <div className="relative z-10 w-full md:max-w-3xl bg-[#121212] border border-[#262626] md:rounded-sm flex flex-col max-h-screen md:max-h-[92vh] overflow-hidden shadow-2xl">
                {/* Header */}
                <div className="flex items-center justify-between px-6 md:px-10 py-5 border-b border-[#262626]">
                    <div className="flex items-center gap-3">
                        <Scissors size={16} className="text-[#d4af37]" />
                        <span className="text-xs tracking-widest uppercase font-semibold text-[#f5f5f0]">
                            {step === 3
                                ? "Reserva Confirmada"
                                : `Reservar · Passo ${step} de ${totalSteps}`}
                        </span>
                    </div>
                    <button
                        onClick={onClose}
                        data-testid="booking-close-btn"
                        className="text-[#a3a3a3] hover:text-[#f5f5f0] transition-colors p-2 -m-2"
                        aria-label="Fechar"
                    >
                        <X size={22} />
                    </button>
                </div>

                {/* Step dots (hidden on success) */}
                {step !== 3 && (
                    <div className="px-6 md:px-10 pt-5">
                        <div className="flex items-center gap-2">
                            {[1, 2].map((i) => (
                                <div
                                    key={i}
                                    className={`step-dot h-[3px] flex-1 ${
                                        i <= step
                                            ? "bg-[#d4af37]"
                                            : "bg-[#262626]"
                                    }`}
                                />
                            ))}
                        </div>
                    </div>
                )}

                {/* Body */}
                <div className="flex-1 overflow-y-auto px-6 md:px-10 py-8">
                    {step === 1 && (
                        <Step1
                            services={siteConfig.services}
                            selected={service}
                            onSelect={setService}
                        />
                    )}
                    {step === 2 && (
                        <Step2
                            service={service}
                            calcomUser={siteConfig.calcom.username}
                        />
                    )}
                    {step === 3 && (
                        <StepSuccess
                            service={service}
                            booking={bookingResult}
                        />
                    )}
                </div>

                {/* Footer actions */}
                <div className="px-6 md:px-10 py-5 border-t border-[#262626] flex items-center justify-between bg-[#0e0e0e]">
                    {step === 1 && (
                        <>
                            <button
                                onClick={back}
                                disabled
                                data-testid="booking-back-btn"
                                className="inline-flex items-center gap-2 text-sm tracking-wide uppercase text-[#a3a3a3] hover:text-[#f5f5f0] disabled:opacity-30 disabled:cursor-not-allowed transition-colors py-2"
                            >
                                <ArrowLeft size={16} />
                                <span>Voltar</span>
                            </button>
                            <button
                                disabled={!service}
                                onClick={next}
                                data-testid="booking-next-step1-btn"
                                className="inline-flex items-center gap-2 px-6 py-3 bg-[#d4af37] text-[#0a0a0a] text-sm font-semibold tracking-wide uppercase rounded-sm hover:bg-[#b3912e] disabled:opacity-40 disabled:cursor-not-allowed transition-all min-h-[44px]"
                            >
                                <span>Continuar</span>
                                <ArrowRight size={16} />
                            </button>
                        </>
                    )}

                    {step === 2 && (
                        <>
                            <button
                                onClick={back}
                                data-testid="booking-back-btn"
                                className="inline-flex items-center gap-2 text-sm tracking-wide uppercase text-[#a3a3a3] hover:text-[#f5f5f0] transition-colors py-2"
                            >
                                <ArrowLeft size={16} />
                                <span>Voltar</span>
                            </button>
                            <p className="hidden md:block text-xs text-[#737373] text-right max-w-xs">
                                Carrega em <span className="text-[#d4af37] font-semibold">Confirmar</span> no calendário acima para finalizares a reserva.
                            </p>
                        </>
                    )}

                    {step === 3 && (
                        <button
                            onClick={onClose}
                            data-testid="booking-close-success-btn"
                            className="ml-auto inline-flex items-center gap-2 px-6 py-3 bg-[#d4af37] text-[#0a0a0a] text-sm font-semibold tracking-wide uppercase rounded-sm hover:bg-[#b3912e] transition-all min-h-[44px]"
                        >
                            <span>Fechar</span>
                            <Check size={16} />
                        </button>
                    )}
                </div>
            </div>
        </div>
    );
};

/* ---------- STEPS ---------- */

const Step1 = ({ services, selected, onSelect }) => (
    <div data-testid="booking-step-1">
        <h3 className="font-display text-3xl md:text-4xl font-bold text-[#f5f5f0] mb-2">
            Escolhe o teu serviço.
        </h3>
        <p className="text-sm text-[#a3a3a3] mb-8">
            Carrega no que procuras hoje.
        </p>

        <div className="space-y-3">
            {services.map((s) => {
                const active = selected?.id === s.id;
                return (
                    <button
                        key={s.id}
                        onClick={() => onSelect(s)}
                        data-testid={`booking-service-${s.id}`}
                        className={`w-full text-left px-5 py-4 border transition-all duration-300 ${
                            active
                                ? "border-[#d4af37] bg-[#d4af37]/10"
                                : "border-[#262626] hover:border-[#3a3a3a] bg-[#0f0f0f]"
                        }`}
                    >
                        <div className="flex items-center gap-4">
                            <div
                                className={`w-5 h-5 rounded-full border-2 flex items-center justify-center shrink-0 ${
                                    active
                                        ? "border-[#d4af37] bg-[#d4af37]"
                                        : "border-[#3a3a3a]"
                                }`}
                            >
                                {active && (
                                    <Check
                                        size={12}
                                        className="text-[#0a0a0a]"
                                    />
                                )}
                            </div>
                            <div className="flex-1">
                                <div className="font-display text-lg md:text-xl font-semibold text-[#f5f5f0]">
                                    {s.name}
                                </div>
                                <div className="flex items-center gap-3 text-xs text-[#737373] mt-1">
                                    <Clock size={12} />
                                    <span>{s.duration} min</span>
                                    <span className="text-[#3a3a3a]">·</span>
                                    <span className="text-[#a3a3a3]">
                                        {s.description}
                                    </span>
                                </div>
                            </div>
                            <div className="font-display text-xl font-bold text-[#d4af37] tabular-nums">
                                {s.price}€
                            </div>
                        </div>
                    </button>
                );
            })}
        </div>
    </div>
);

const Step2 = ({ service, calcomUser }) => {
    // Deep-link directly to the matching event type when available, so the
    // client doesn't need to choose the service twice.
    const eventSlug = service?.id || "";
    const calLink = eventSlug ? `${calcomUser}/${eventSlug}` : calcomUser;

    return (
        <div data-testid="booking-step-2">
            <h3 className="font-display text-3xl md:text-4xl font-bold text-[#f5f5f0] mb-2">
                Escolhe o horário.
            </h3>
            <p className="text-sm text-[#a3a3a3] mb-4">
                Serviço:{" "}
                <span className="text-[#d4af37] font-semibold">
                    {service?.name}
                </span>{" "}
                · {service?.duration} min · {service?.price}€
            </p>

            {/* Mobile-only hint banner */}
            <div className="md:hidden mb-4 px-4 py-3 border border-[#d4af37]/40 bg-[#d4af37]/10 text-xs text-[#f5f5f0] leading-relaxed">
                Carrega em <span className="text-[#d4af37] font-semibold">Confirmar</span> no calendário em baixo para finalizares a reserva.
            </div>

            <div className="relative border border-[#262626] bg-[#0a0a0a] overflow-hidden">
                <Cal
                    calLink={calLink}
                    data-testid="booking-calcom-embed"
                    style={{
                        width: "100%",
                        height: "100%",
                        minHeight: "560px",
                        overflow: "scroll",
                    }}
                    config={{
                        theme: "dark",
                        layout: "month_view",
                    }}
                />
            </div>

            <p className="mt-5 text-xs text-[#737373] leading-relaxed">
                Após confirmares no calendário acima, vais receber um email
                com a confirmação e um lembrete 24h antes da marcação.
            </p>
        </div>
    );
};

const StepSuccess = ({ service, booking }) => {
    const startTime = booking?.startTime || booking?.date;
    const formattedDate = startTime
        ? new Date(startTime).toLocaleString("pt-PT", {
              weekday: "long",
              day: "2-digit",
              month: "long",
              hour: "2-digit",
              minute: "2-digit",
          })
        : null;

    return (
        <div
            data-testid="booking-step-success"
            className="text-center py-6 md:py-10"
        >
            <div className="inline-flex items-center justify-center w-20 h-20 rounded-full bg-[#d4af37]/15 border border-[#d4af37]/40 mb-6">
                <PartyPopper size={36} className="text-[#d4af37]" />
            </div>

            <h3 className="font-display text-3xl md:text-4xl font-bold text-[#f5f5f0] mb-3">
                Reserva confirmada!
            </h3>
            <p className="text-sm md:text-base text-[#a3a3a3] mb-8 max-w-md mx-auto">
                Até já na Barbearia 18. Estamos à tua espera.
            </p>

            <div className="max-w-md mx-auto bg-[#0f0f0f] border border-[#262626] px-5 py-5 text-left space-y-3">
                {service?.name && (
                    <div className="flex items-start gap-3">
                        <Scissors
                            size={16}
                            className="text-[#d4af37] mt-0.5 shrink-0"
                        />
                        <div>
                            <div className="text-[10px] uppercase tracking-widest text-[#737373]">
                                Serviço
                            </div>
                            <div className="text-sm text-[#f5f5f0] font-medium">
                                {service.name}
                            </div>
                        </div>
                    </div>
                )}
                {formattedDate && (
                    <div className="flex items-start gap-3">
                        <Clock
                            size={16}
                            className="text-[#d4af37] mt-0.5 shrink-0"
                        />
                        <div>
                            <div className="text-[10px] uppercase tracking-widest text-[#737373]">
                                Quando
                            </div>
                            <div className="text-sm text-[#f5f5f0] font-medium capitalize">
                                {formattedDate}
                            </div>
                        </div>
                    </div>
                )}
                <div className="flex items-start gap-3">
                    <Mail
                        size={16}
                        className="text-[#d4af37] mt-0.5 shrink-0"
                    />
                    <div>
                        <div className="text-[10px] uppercase tracking-widest text-[#737373]">
                            Confirmação
                        </div>
                        <div className="text-sm text-[#f5f5f0] font-medium">
                            Enviámos os detalhes para o teu email.
                        </div>
                        <div className="text-xs text-[#737373] mt-1">
                            Verifica a pasta de spam se não chegar.
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
};
